import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Skeleton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { EmailPreview } from './EmailPreview';
import { useIsMobile } from 'hooks/useIsMobile';

/**
 * @component ExpandedRowContent
 * @description Wrapper component for expanded row content with loading/error/empty states
 * @param {Object} props
 * @param {boolean} props.loading - Loading state
 * @param {string} [props.error] - Error message
 * @param {Function} [props.onRetry] - Retry handler for error state
 * @param {boolean} [props.isEmpty] - Whether content is empty
 * @param {string} [props.emptyMessage='No details available'] - Message for empty state
 * @param {string} [props.htmlContent] - Email HTML content for preview
 * @param {React.ReactNode} props.children - Main content
 * @param {boolean} [props.showEmailPreview=true] - Whether to show email preview column
 * @param {string} [props.bgColor='white'] - Background color
 * @returns {JSX.Element}
 */
export function ExpandedRowContent({
  loading,
  error,
  onRetry,
  isEmpty,
  emptyMessage = 'No details available',
  htmlContent,
  children,
  showEmailPreview = true,
  bgColor = 'white',
}) {
  const isMobile = useIsMobile();

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: bgColor, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading details...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3, bgcolor: bgColor }}>
        <Alert 
          severity="error"
          action={
            onRetry && (
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
              >
                Retry
              </Button>
            )
          }
        >
          Failed to load details: {error}
        </Alert>
      </Box>
    );
  }

  // Empty state
  if (isEmpty) {
    return (
      <Box sx={{ p: 3, bgcolor: bgColor, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  // Content with optional email preview
  if (showEmailPreview && htmlContent !== undefined) {
    return (
      <Box sx={{ bgcolor: bgColor }}>
        <Grid container spacing={3}>
          {/* Left Side: Email Preview */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle1" fontWeight="600">
                Email Preview
              </Typography>
            </Box>
            <Paper variant="outlined" sx={{ minHeight: isMobile ? 250 : 350 }}>
              <EmailPreview htmlContent={htmlContent} />
            </Paper>
          </Grid>

          {/* Right Side: Details */}
          <Grid item xs={12} md={6}>
            {children}
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Content without email preview
  return (
    <Box sx={{ bgcolor: bgColor }}>
      {children}
    </Box>
  );
}

/**
 * @component ExpandedRowSkeleton
 * @description Loading skeleton for expanded row content
 * @param {Object} props
 * @param {boolean} [props.showEmailPreview=true] - Whether to show email preview skeleton
 * @returns {JSX.Element}
 */
export function ExpandedRowSkeleton({ showEmailPreview = true }) {
  return (
    <Box sx={{ bgcolor: 'white' }}>
      <Grid container spacing={3}>
        {showEmailPreview && (
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={350} />
          </Grid>
        )}
        <Grid item xs={12} md={showEmailPreview ? 6 : 12}>
          <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="70%" height={20} sx={{ mb: 3 }} />
          
          <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={100} sx={{ mb: 3 }} />
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Skeleton variant="rectangular" width={120} height={36} />
            <Skeleton variant="rectangular" width={120} height={36} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * @component DetailSection
 * @description A titled section within expanded row content
 * @param {Object} props
 * @param {string} props.title - Section title
 * @param {React.ReactNode} props.children - Section content
 * @param {Object} [props.sx] - Additional styles
 * @returns {JSX.Element}
 */
export function DetailSection({ title, children, sx = {} }) {
  return (
    <Box sx={{ mb: 3, ...sx }}>
      <Typography variant="subtitle1" fontWeight="600" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

